/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { Button, Colors, Icon, ZapierLogo } from '@zapier/design-system';
import { labelStyles } from '@zapier/style-encapsulation';
import { useState } from 'react';
import { MEDIA_MAX_WIDTH, MEDIA_MIN_WIDTH } from '../../../constants';
import {
  CTAType,
  EventAction,
  InteractionName,
  useAvroTracking,
} from '../../../observability';
import { CustomBrandButton } from '../../CustomBrandButton';

export type HeaderProps = {
  links?: { text: string; url: string }[];
  button?: { text: string; url: string };
  signupButtonText?: string;
  loginButtonText?: string;
};

const Styles = labelStyles('Header', {
  root: css({
    position: 'fixed',
    top: 0,
    zIndex: 100,
    width: '100%',
    height: '60px',
    padding: '0 20px',
    boxSizing: 'border-box',
    borderBottom: `1px solid ${Colors.earth}`,
    backgroundColor: Colors.white,
    [MEDIA_MIN_WIDTH.medium]: {
      height: '80px',
    },
  }),

  content: css({
    height: '100%',
    maxWidth: '1170px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  }),

  left: css({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',

    [MEDIA_MIN_WIDTH.extraSmall]: {
      gap: '12px',
    },
  }),

  mobileNavButton: css({
    display: 'flex',
    padding: 0,
    margin: 0,
    background: 'none',
    border: 'none',
    color: Colors.neutral600,
    cursor: 'pointer',
    position: 'relative',

    '&:hover': {
      color: Colors.neutral800,
    },

    '&:focus-visible': {
      outline: 'none',

      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        top: '-6px',
        right: '-6px',
        bottom: '-6px',
        left: '-6px',
        border: `2px solid ${Colors.earth}`,
        borderRadius: '12px',
      },
    },

    [MEDIA_MIN_WIDTH.medium]: {
      display: 'none',
    },
  }),

  logo: css({
    textDecoration: 'none',
    position: 'relative',

    '&:focus-visible': {
      outline: 'none',

      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        top: '-8px',
        right: '-12px',
        bottom: '-8px',
        left: '-12px',
        border: `2px solid ${Colors.earth}`,
        borderRadius: '12px',
      },
    },

    '@media (max-width: 368px)': {
      display: 'none',
    },

    [MEDIA_MAX_WIDTH.extraSmall]: {
      '& > svg': {
        maxWidth: '96px !important',
      },
    },
  }),

  right: css({
    display: 'flex',
    alignItems: 'center',
    gap: '30px',
  }),

  nav: css({
    '@media (max-width: 767px)': {
      width: '100%',
      height: 'calc(100% - 60px)',
      padding: '40px 20px',
      boxSizing: 'border-box',
      position: 'fixed',
      top: '60px',
      left: '0px',
      zIndex: 10,
      backgroundColor: Colors.white,
      overflowY: 'auto',

      '&[data-active="false"]': {
        display: 'none',
      },
    },
  }),

  list: css({
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    listStyle: 'none',

    [MEDIA_MIN_WIDTH.medium]: {
      flexDirection: 'row',
      gap: '25px',
    },
    '@media (min-width: 805px)': {
      gap: '30px',
    },
  }),

  link: css({
    fontFamily: '"Chivo Mono", "Inter", sans-serif',
    fontSize: '19px',
    letterSpacing: '8%',
    textTransform: 'uppercase',
    color: Colors.earth,
    textDecoration: 'none',
    position: 'relative',

    '&:hover': {
      fontWeight: 600,
    },

    '&:focus-visible': {
      outline: 'none',

      '&::before': {
        content: '""',
        position: 'absolute',
        pointerEvents: 'none',
        top: '-8px',
        right: '-12px',
        bottom: '-8px',
        left: '-12px',
        border: `2px solid ${Colors.earth}`,
        borderRadius: '18px',
      },
    },
  }),

  mobileOnlyItems: css({
    marginTop: '40px',
    paddingTop: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    borderTop: `1px solid ${Colors.neutral300}`,

    [MEDIA_MIN_WIDTH.medium]: {
      display: 'none',
    },
  }),
});

export function Header(props: HeaderProps) {
  const [showMobileNav, setShowMobileNav] = useState(false);
  const {
    emitCTAClickEvent,
    emitUserInteractionEvent,
    emitSignupUserInteractionEvent,
  } = useAvroTracking();

  const toggleMobileNav = () => {
    setShowMobileNav((prevState) => {
      document.body.style.overflow = prevState ? '' : 'hidden';
      return !prevState;
    });
  };

  const hideMobileNav = () => {
    document.body.style.overflow = '';
    setShowMobileNav(false);
  };

  return (
    <header css={Styles.root}>
      <div css={Styles.content}>
        <div css={Styles.left}>
          <button
            css={Styles.mobileNavButton}
            aria-label="Toggle navigation menu"
            aria-expanded={showMobileNav}
            aria-controls="zapconnect-nav"
            onClick={toggleMobileNav}
          >
            <Icon
              name={showMobileNav ? 'formX' : 'navMenu'}
              color="currentColor"
              size={24}
            />
          </button>
          <a css={Styles.logo} href="/" aria-label="Go to the Zapier Homepage">
            <ZapierLogo width={112} height={32} />
          </a>
        </div>
        <div css={Styles.right}>
          <nav css={Styles.nav} id="zapconnect-nav" data-active={showMobileNav}>
            <ul css={Styles.list}>
              {props.links?.map((link) => (
                <li key={link.text}>
                  <a
                    css={Styles.link}
                    href={link.url}
                    onClick={() => {
                      hideMobileNav();
                      emitUserInteractionEvent({
                        event_action: EventAction.click,
                        interaction_name: InteractionName.navigation,
                        interaction_raw_text: link.text,
                        option_selected: 'nav_link',
                        destination_href: link.url,
                      });
                    }}
                  >
                    {link.text}
                  </a>
                </li>
              ))}
            </ul>
            <div css={Styles.mobileOnlyItems}>
              <Button
                href="https://zapier.com/app/login"
                color="secondary"
                isFullWidth={true}
                onClick={() => {
                  hideMobileNav();
                  emitCTAClickEvent({
                    text: 'Log into Zapier',
                    href: 'https://zapier.com/app/login',
                    section: 'Header',
                    type: CTAType.cta_button,
                  });
                }}
              >
                {props.loginButtonText}
              </Button>
              <Button
                href="https://zapier.com/sign-up/"
                color="primary"
                isFullWidth={true}
                onClick={() => {
                  hideMobileNav();
                  emitSignupUserInteractionEvent({
                    absolute_url: 'https://zapier.com/sign-up/',
                    signup_funnel: 'signup_intent',
                    signup_type: 'direct',
                  });
                }}
              >
                {props.signupButtonText}
              </Button>
            </div>
          </nav>
          {props.button?.text && props.button.url && (
            <CustomBrandButton
              variant="zapconnect"
              size="small"
              href={props.button.url}
              onClick={() => {
                hideMobileNav();
                emitCTAClickEvent({
                  text: props.button?.text ?? '',
                  href: props.button?.url ?? '',
                  section: 'Header',
                  type: CTAType.cta_button,
                });
              }}
            >
              {props.button.text}
            </CustomBrandButton>
          )}
        </div>
      </div>
    </header>
  );
}
